import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { getPersonalApiKeyIssue } from "components/ApiKeys/PersonalApiKeys/helpers";
import usePersonalApiKeysState from "components/ApiKeys/PersonalApiKeys/usePersonalApiKeysState";
import NotFoundPage from "components/error/NotFoundPage";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import { ApiKeyDisabledReason, AuthorizationScheme } from "types/generated";
import { UserManagementActivationStatus } from "views/Account/Settings/types";

import PersonalSettingsApiKeysPageLayout from "./components/PageLayout";
import { showPersonalSettingsApiKeysCreateDrawer } from "./CreateDrawer";
import PersonalSettingsApiKeysEmpty from "./Empty";
import { GET_PERSONAL_API_KEYS } from "./gql";
import { showPersonalSettingsApiKeysInfoDrawer } from "./InfoDrawer";
import { PERSONAL_API_KEYS_TABLE_CELLS } from "./Table/Cells";
import {
  PERSONAL_API_KEYS_FILTERS_ORDER_KEY,
  PERSONAL_API_KEYS_SORT_CONFIG,
} from "./Table/constants";
import usePersonalApiKeysSort from "./Table/useSort";
import usePersonalApiKeysTableStore from "./Table/useTableStore";
import usePersonalSettingsApiKeysDetailsFromUrl from "./useDetailsFromUrl";

type PersonalSettingsApiKeysProps = {
  authorizationScheme: string;
};

const REFETCH_QUERIES = [GET_PERSONAL_API_KEYS];

const PersonalSettingsApiKeys = ({ authorizationScheme }: PersonalSettingsApiKeysProps) => {
  const { data, loading, error } = useQuery(GET_PERSONAL_API_KEYS, {
    fetchPolicy: "no-cache",
  });
  const { enabled: isPersonalApiKeysEnabled } = usePersonalApiKeysState();
  const activeRowId = useActiveDrawerId();

  const activationStatus =
    authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const handleOpenInfoDrawer = () => {
    showPersonalSettingsApiKeysInfoDrawer();
  };

  const handleCreateApiKeyDrawer = () => {
    showPersonalSettingsApiKeysCreateDrawer({
      refetchQueries: REFETCH_QUERIES,
    });
  };

  const apiKeys = useMemo(() => data?.viewer?.ownedApiKeys || [], [data?.viewer?.ownedApiKeys]);
  const sortedApiKeys = usePersonalApiKeysSort(apiKeys);
  usePersonalSettingsApiKeysDetailsFromUrl(apiKeys, loading || !data?.viewer?.ownedApiKeys);

  const isManagedUsersActive = activationStatus === UserManagementActivationStatus.ACTIVE;
  const shouldShowManagedUsersWarning = !isManagedUsersActive;

  const hasApiKeys = apiKeys.length > 0;
  const hasWorkingKeys = apiKeys.some((apiKey) => !getPersonalApiKeyIssue(apiKey));
  const hasKeysDisabledByAccount = apiKeys.some(
    (apiKey) => apiKey.disabledReason === ApiKeyDisabledReason.Account
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.viewer?.ownedApiKeys) {
    return (
      <PersonalSettingsApiKeysPageLayout showManagedUsersWarning={shouldShowManagedUsersWarning}>
        <PageLayoutSkeleton />
      </PersonalSettingsApiKeysPageLayout>
    );
  }

  if (!data?.viewer?.ownedApiKeys) {
    return <NotFoundPage />;
  }

  return (
    <PersonalSettingsApiKeysPageLayout
      onOpenInfoDrawer={handleOpenInfoDrawer}
      onOpenCreateApiKeyDrawer={handleCreateApiKeyDrawer}
      seats={data.seats.apiKey}
      showManagedUsersWarning={shouldShowManagedUsersWarning}
      isPersonalApiKeysTurnedOff={isPersonalApiKeysEnabled === false}
      hasWorkingKeys={hasWorkingKeys}
      hasKeysDisabledByAccount={hasKeysDisabledByAccount}
      hasApiKeys={hasApiKeys}
    >
      <ListContentWrapper>
        {shouldShowManagedUsersWarning && !hasApiKeys && (
          <PersonalSettingsApiKeysEmpty isUnavailable />
        )}
        {isManagedUsersActive && !hasApiKeys && (
          <PersonalSettingsApiKeysEmpty
            onCreate={handleCreateApiKeyDrawer}
            seats={data.seats.apiKey}
          />
        )}
        {/* After switching to Login Policies the backend deactivates existing keys, but they stay listed so they can be deleted */}
        {hasApiKeys && (
          <TableSimple
            data={sortedApiKeys}
            activeRowId={activeRowId}
            store={usePersonalApiKeysTableStore}
            ariaLabel="Personal API keys table"
            cellPerColumn={PERSONAL_API_KEYS_TABLE_CELLS}
            sortConfig={PERSONAL_API_KEYS_SORT_CONFIG}
            filtersStorageKey={PERSONAL_API_KEYS_FILTERS_ORDER_KEY}
            hideSettings
            hasPagination={false}
          />
        )}
      </ListContentWrapper>
    </PersonalSettingsApiKeysPageLayout>
  );
};

export default PersonalSettingsApiKeys;
